import dayjs from 'dayjs';

// Utils

import { normalizeNameOfPerson } from '../normalize/normalizeNameOfPerson';
import { householdMemberTools } from '../tools/householdMemberTools';
import { getDateDifference } from '../dates/date.utils';

// Constants

import { BLOOD_RELATIONSHIP_TYPES } from '../../constants/ssap/hhmTypes/bloodRelationshipCodesList';
import { THC_TYPES } from '../../constants/ssap/hhmTypes/taxRelationsTypes';

export const familyAnalyzer = config => {

    const {
        bloodRelationships,
        bloodRelationshipCodes,
        currentMemberIndex,
        householdMembers,
        isState,
        loadedRelations,
        primaryTaxFilerPersonId,
        serverDate
    } = config;

    const currentHhmGuid = householdMemberTools
        .extractFromHouseholdMembers(householdMembers)
        .getGuidByIndexInArray(currentMemberIndex);

    const {
        dateOfBirth,
    } = (hhm => hhm ? hhm : { })(householdMembers.find(hhm => hhm.applicantGuid === currentHhmGuid));

    const bloodRelationshipsCodes = (() => {
        let output = {};
        const RELATIONS = [
            {
                type: 'spouse',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.SPOUSE
                ]
            },
            {
                type: 'parents',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.PARENT_OF_CHILD,
                    BLOOD_RELATIONSHIP_TYPES.STEPPARENT
                ]
            },
            {
                type: 'parentsForAgeValidation',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.PARENT_OF_CHILD
                ]
            },
            {
                type: 'children',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.CHILD,
                    BLOOD_RELATIONSHIP_TYPES.ADOPTED_CHILD,
                    BLOOD_RELATIONSHIP_TYPES.STEPCHILD
                ]
            },
            {
                type: 'childrenForAgeValidation',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.CHILD,
                    BLOOD_RELATIONSHIP_TYPES.ADOPTED_CHILD
                ]
            },
            {
                type: 'parentOrCaretakerForEsc',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.PARENT_OF_CHILD,
                    BLOOD_RELATIONSHIP_TYPES.STEPPARENT,
                    BLOOD_RELATIONSHIP_TYPES.GRANDPARENT
                ]
            }
        ];
        loadedRelations.forEach(lr => {
            RELATIONS.forEach(r => {
                const {
                    type,
                    enums
                } = r;
                if(enums.some(e => e === lr.type)) {
                    if(output[type]) {
                        output = {
                            ...output,
                            [type]: [
                                ...output[type],
                                lr.code
                            ]
                        };
                    } else {
                        output = {
                            ...output,
                            [type]: [
                                lr.code
                            ]
                        };
                    }
                }

            });
        });
        return output;
    })();

    const relatedPersonsIds = (() => {
        let output = {};
        Object.entries(bloodRelationshipsCodes).forEach(([ codeType, values ]) => {
            let res = [];
            bloodRelationships.forEach(br => {
                if(br.relatedPersonGuid === currentHhmGuid && values.some(v => v === br.relation)) {
                    res.push(+br.individualPersonId);
                }
            });

            if(res.length > 0) {
                output = {
                    ...output,
                    [codeType]: res
                };
            } else {
                output = {
                    ...output,
                    [codeType]: null
                };
            }
        });
        return output;
    })();

    const relatedPersonsGuids = (() => {
        let output = {};
        Object.entries(bloodRelationshipsCodes).forEach(([ codeType, values ]) => {
            let res = [];

            bloodRelationships.forEach(br => {
                if(br.relatedPersonGuid === currentHhmGuid && values.some(v => v === br.relation)) {
                    res.push(br.personGuid);
                }
            });

            if(res.length > 0) {
                output = {
                    ...output,
                    [codeType]: res
                };
            } else {
                output = {
                    ...output,
                    [codeType]: null
                };
            }
        });
        return output;
    })();

    let familyInfo = {
        currentHhm: {},
        relatives: [],
        dependentsOfCaretaker: []
    };

    householdMembers.forEach((hhm, idx) => {

        const establishRelation = guid => {

            const getRelation = guid => {
                const relation = bloodRelationships.find(br => br.personGuid === guid && br.relatedPersonGuid === currentHhmGuid);
                return relation ? relation : {};
            };

            let establishedRelation = loadedRelations.find(l => l.code === getRelation(guid).relation);

            return establishedRelation ? establishedRelation : {};
        };

        const {
            code,
            text
        } = establishRelation(hhm.applicantGuid);

        const summary = {
            personName: normalizeNameOfPerson(hhm.name),
            personId: hhm.personId,
            personGuid: hhm.applicantGuid,
            personIndex: idx,
            addressId: hhm.addressId,
            relationToCurrentHhm: text,
            relationCode: code,
            applyingForCoverage: hhm.applyingForCoverageIndicator,
            dateOfBirth: hhm.dateOfBirth,
            isSsnProvided: !!hhm.socialSecurityCard.socialSecurityNumber
        };

        idx === currentMemberIndex ? familyInfo.currentHhm = summary : familyInfo.relatives.push(summary);

        const checkIfCaretakersDependent = (idx, hhms, serverDate) => {

            if(
                getDateDifference(serverDate, hhms[idx].dateOfBirth, 'years') < 19 ||
                !hhms[idx].applyingForCoverageIndicator
            ) {
                return false;
            }

            const relationBetweenHouseholdMembers = bloodRelationships.find(relation => {
                const { personGuid, relatedPersonGuid } = relation;

                return hhm.applicantGuid === personGuid && hhms[idx].applicantGuid === relatedPersonGuid && personGuid !== relatedPersonGuid;
            });

            if(!relationBetweenHouseholdMembers) return false;

            const { relation } = relationBetweenHouseholdMembers;

            if(relation === null) return false;

            const caretakersDependentCriteria = [];

            // 1st criteria: age is less than 19
            caretakersDependentCriteria.push(getDateDifference(serverDate, hhm.dateOfBirth, 'years') < 19);

            // 2nd criteria: live at the same address
            caretakersDependentCriteria.push(hhm.addressId === hhms[idx].addressId);

            // 3rd criteria: they are not spouses, former spouses or domestic partners
            caretakersDependentCriteria.push(
                Object
                    .values(bloodRelationshipCodes.caretakerNotConsidered)
                    .every(code => code !== relation)
            );

            return caretakersDependentCriteria.every(criteria => criteria === true);
        };

        if(idx !== currentMemberIndex && checkIfCaretakersDependent(currentMemberIndex, householdMembers, serverDate)) {
            familyInfo.dependentsOfCaretaker.push(summary);
        }
    });

    const isOlderThanMarriageAge = getDateDifference(serverDate, dateOfBirth, 'years') >= 14;
    const isOlderThanChildAge = getDateDifference(serverDate, dateOfBirth, 'years') >= 19;

    const checkAgeToRelatives = (guids, compareMethod) => {
        if(!guids) {
            return null;
        }
        if(!dayjs()[compareMethod]) {
            console.log('Incorrect compare method is provided');
            return null;
        }
        return guids.reduce(
            (result, guid) => {
                const relative = familyInfo.relatives.find(r => r.personGuid === guid);
                const correct = dayjs(familyInfo.currentHhm.dateOfBirth)[compareMethod](dayjs(relative.dateOfBirth));
                switch(true){
                    case !correct:
                        switch(true) {
                            case result:
                                return [ ...result, relative.personId ];
                            default:
                                return [ relative.personId ];
                        }
                    default:
                        return result;
                }
            },
            null
        );
    };

    const isOlderThanChildren = checkAgeToRelatives(
        (isState.NV || isState.NJ)
            ? relatedPersonsGuids.childrenForAgeValidation
            : relatedPersonsGuids.children,
        'isBefore'
    );
    const isYoungerThanParents = checkAgeToRelatives(
        (isState.NV || isState.NJ)
            ? relatedPersonsGuids.parentsForAgeValidation
            : relatedPersonsGuids.parents,
        'isAfter'
    );

    const hasParentOrCaretakersWithEsc = (() => {
        return householdMembers.some(hhm => {
            return !!hhm.employerSponsoredCoverage.length
                && hhm.applyingForCoverageIndicator
                && relatedPersonsGuids.parentOrCaretakerForEsc
                && relatedPersonsGuids.parentOrCaretakerForEsc.includes(hhm.applicantGuid);
        });
    })();

    const spouseName = (spouse => spouse ? spouse.personName : null)(familyInfo.relatives.find(r => bloodRelationshipsCodes.spouse && bloodRelationshipsCodes.spouse.some(br => br === r.relationCode)));
    const spouseIndex = (spouse => spouse ? familyInfo.relatives.find(r => r.personName === spouseName).personIndex : null)(spouseName);
    const primaryTaxFilerHouseholdMembers = (() => {
        const getHouseholdId = (hhm => hhm ? hhm.householdId : null)(householdMembers.find(hhm => hhm.personId === primaryTaxFilerPersonId));
        return (id => id !== null ? householdMembers.filter(hhm => {

            const {
                taxHouseholdComposition: {
                    taxRelationship
                }
            } = hhm;
            return hhm.householdId === id && taxRelationship !== THC_TYPES.UNSPECIFIED;
        }) : [])(getHouseholdId);
    })();

    const isSpouseProvidedSsn = (() => {
        if(spouseIndex === null) return false;
        return familyInfo.relatives.find(r => r.personIndex === spouseIndex).isSsnProvided;
    })();

    familyInfo = {
        ...familyInfo,
        currentHhm: {
            ...familyInfo.currentHhm,
            spouseIndex,
            spouseName,
            isOlderThanMarriageAge,
            isOlderThanChildAge,
            isOlderThanChildren,
            isSpouseProvidedSsn,
            isYoungerThanParents,
            hasParentOrCaretakersWithEsc
        },
        relatedPersonsIds,
        relatedPersonsGuids,
        primaryTaxFilerHouseholdMembers
    };

    return familyInfo;
};
